import { useState, useEffect } from 'react';
import { MapPin, Stethoscope, Smile } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';
import { ExploracionTopograficaView } from './ExploracionTopograficaView';
import { ExamenFisicoView } from './ExamenFisicoView';
import { OdontogramaView } from './OdontogramaView';
import { PeriodontogramaView } from './PeriodontogramaView';

type MainTab = 'topografico' | 'fisico' | 'dental';
type DentalSub = 'odontograma' | 'periodontograma';
type DentalTipo = 'adulto' | 'infantil';

interface Props { id: string; canEdit: boolean; consultaId?: string | null }

export function ExploracionesTab({ id, canEdit, consultaId }: Props) {
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();
  const mostrarOdontograma = moduloAplica('odontograma', perfilTipo, modulosHabilitados);
  const mostrarPeriodontograma = moduloAplica('periodontograma', perfilTipo, modulosHabilitados);
  const mostrarDental = mostrarOdontograma || mostrarPeriodontograma;

  // Gating por perfil de clínica (examen físico/topográfico ya no son universales).
  const mostrarFisico = moduloAplica('examen_fisico', perfilTipo, modulosHabilitados);
  const mostrarTopografico = moduloAplica('mapa_topografico', perfilTipo, modulosHabilitados);

  const [mainTab, setMainTab] = useState<MainTab>('fisico');
  const [dentalSub, setDentalSub] = useState<DentalSub>('odontograma');
  const [dentalTipo, setDentalTipo] = useState<DentalTipo>('adulto');

  useEffect(() => {
    if (dentalSub === 'odontograma' && !mostrarOdontograma && mostrarPeriodontograma) setDentalSub('periodontograma');
    if (dentalSub === 'periodontograma' && !mostrarPeriodontograma && mostrarOdontograma) setDentalSub('odontograma');
  }, [mostrarOdontograma, mostrarPeriodontograma, dentalSub]);

  const mainTabs: { id: MainTab; label: string; icon: typeof MapPin }[] = [
    ...(mostrarFisico ? [{ id: 'fisico' as const, label: 'Examen Físico', icon: Stethoscope }] : []),
    ...(mostrarTopografico ? [{ id: 'topografico' as const, label: 'Topográfico', icon: MapPin }] : []),
    ...(mostrarDental ? [{ id: 'dental' as const, label: 'Dental', icon: Smile }] : []),
  ];

  // Si la pestaña activa deja de estar disponible (perfil profesional sin acceso), cae a la primera visible.
  useEffect(() => {
    if (!mainTabs.some(t => t.id === mainTab) && mainTabs.length > 0) setMainTab(mainTabs[0].id);
  }, [mostrarFisico, mostrarTopografico, mostrarDental]);

  return (
    <div className="space-y-4">
      {/* Main tab selector */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {mainTabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setMainTab(t.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-lg transition-all',
                mainTab === t.id ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              )}>
              <Icon size={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {mainTab === 'fisico' && mostrarFisico && (
        <ExamenFisicoView id={id} canEdit={canEdit} consultaId={consultaId} />
      )}

      {mainTab === 'topografico' && mostrarTopografico && (
        <ExploracionTopograficaView id={id} canEdit={canEdit} consultaId={consultaId} />
      )}

      {mainTab === 'dental' && mostrarDental && (
        <div className="space-y-3">
          {/* Dental controls row */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Odontograma / Periodontograma */}
            <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
              {mostrarOdontograma && (
                <button onClick={() => setDentalSub('odontograma')}
                  className={cn('px-3 py-1.5 transition-colors', dentalSub === 'odontograma' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                  Odontograma
                </button>
              )}
              {mostrarPeriodontograma && (
                <button onClick={() => setDentalSub('periodontograma')}
                  className={cn('px-3 py-1.5 transition-colors', dentalSub === 'periodontograma' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                  Periodontograma
                </button>
              )}
            </div>
            {/* Adulto / Infantil */}
            <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
              <button onClick={() => setDentalTipo('adulto')}
                className={cn('px-3 py-1.5 transition-colors', dentalTipo === 'adulto' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                Adulto (FDI)
              </button>
              <button onClick={() => setDentalTipo('infantil')}
                className={cn('px-3 py-1.5 transition-colors', dentalTipo === 'infantil' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                Infantil (FDI)
              </button>
            </div>
          </div>

          {dentalSub === 'odontograma' && mostrarOdontograma && (
            <div className="overflow-x-auto">
              <OdontogramaView id={id} canEdit={canEdit} tipo={dentalTipo} consultaId={consultaId} />
            </div>
          )}
          {dentalSub === 'periodontograma' && mostrarPeriodontograma && (
            <div className="overflow-x-auto">
              <PeriodontogramaView id={id} canEdit={canEdit} tipo={dentalTipo} consultaId={consultaId} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
